import type { SlideCategory } from "./types";
import type { IntentTag, ThemeColors } from "../service/slideGenerationApi";

/**
 * Structured slide model — the contract the in-app renderer draws from. The
 * backend's finalised JSON should map onto this shape (see `fromApiSlide` seam
 * in index barrel). Coordinates are in slide units on a fixed 16:9 canvas; the
 * renderer scales them to whatever width it's given, preserving layout.
 */

export const SLIDE_CANVAS = { width: 960, height: 540 } as const;

/** Maps the backend IntentTag onto the renderer's slide category. */
const INTENT_TO_CATEGORY: Record<IntentTag, SlideCategory> = {
  cover: "COVER",
  closing: "CLOSING",
  agenda: "OVERVIEW",
  section: "OVERVIEW",
  body: "OVERVIEW",
  list: "OVERVIEW",
  comparison: "COMPARISON",
  swot: "COMPARISON",
  process: "PROCESS",
  timeline: "TIMELINE",
  hierarchical: "HIERARCHICAL",
  statistical: "STATISTICAL",
};

export function intentToCategory(tag?: IntentTag): SlideCategory | undefined {
  return tag ? INTENT_TO_CATEGORY[tag] : undefined;
}

export type TextAlign = "left" | "center" | "right" | "justify";
export type VAlign = "top" | "middle" | "bottom";

interface ElementBase {
  left: number;
  top: number;
  width: number;
  height: number;
  rotation?: number;
}

export interface TextElement extends ElementBase {
  type: "text";
  text?: string;
  bullets?: string[];
  fontSize?: number;
  fontColor?: string;
  fontName?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  align?: TextAlign;
  valign?: VAlign;
  fill?: string;
}

export interface ImageElement extends ElementBase {
  type: "image";
  src: string;
  alt?: string;
  fit?: "contain" | "cover" | "fill";
}

export type ShapeKind =
  | "rectangle"
  | "roundedRectangle"
  | "ellipse"
  | "diamond"
  | "triangle"
  | "rightArrow"
  | "chevron"
  | "pentagon"
  | "star";

export interface ShapeElement extends ElementBase {
  type: "shape";
  shape: ShapeKind;
  fill?: string;
  borderColor?: string;
  borderWidth?: number;
  text?: string;
  fontSize?: number;
  fontColor?: string;
}

export type ChartKind = "column" | "bar" | "line" | "pie" | "donut";

export interface ChartSeries {
  name?: string;
  data: number[];
}

export interface ChartElement extends ElementBase {
  type: "chart";
  chart: ChartKind;
  series: ChartSeries[];
  categories?: string[];
  colors?: string[];
  title?: string;
}

export interface TableElement extends ElementBase {
  type: "table";
  rows: string[][];
  header?: boolean;
}

/** Anything the backend sends that the renderer doesn't know → drawn as a labelled placeholder. */
export interface UnsupportedElement extends ElementBase {
  type: "unsupported";
  label?: string;
}

export type SlideElement =
  | TextElement
  | ImageElement
  | ShapeElement
  | ChartElement
  | TableElement
  | UnsupportedElement;

export interface SlideModel {
  id?: string;
  title?: string;
  background?: string;
  elements: SlideElement[];
}

export interface DeckModel {
  title?: string;
  slides: SlideModel[];
}

const BLUE = "#0B5DAA";
const ACCENT = "#F5A623";
const TEAL = "#1A9E8F";
const PLUM = "#A63D7D";
/** Single source of the 6-colour brand chart palette (shared by the Office-side
 *  renderer in insertSlideModel and the SVG preview in SlidePreview). */
export const CHART_PALETTE = [BLUE, ACCENT, TEAL, PLUM, "#5C3370", "#D32F2F"];
const INK = "#1A2330";
const PANEL = "#F4F7FB";
const PANEL_BORDER = "#E3EAF2";
const LIGHT_BLUE = "#1E6FB8";

/** Deterministic pseudo-values from a seed string (no Math.random — stable for tests). */
function pseudoSeries(seed: string, n: number): number[] {
  const out: number[] = [];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + (seed.codePointAt(i) ?? 0)) % 997;
  for (let i = 0; i < n; i++) {
    h = (h * 31 + 17) % 997;
    out.push(20 + (h % 80));
  }
  return out;
}

const titleEl = (text: string): TextElement => ({
  type: "text",
  text,
  left: 48,
  top: 40,
  width: 864,
  height: 80,
  fontSize: 34,
  bold: true,
  fontColor: "#1A1A1A",
  align: "left",
});

/**
 * The resolved colour roles a slide layout draws with. Derived from the active
 * deck's theme (buildPalette) so generated slides match the deck's brand, with
 * the built-in blue/gold scheme as the fallback.
 */
export interface SlidePalette {
  primary: string; // headers, rails, cover background
  accent: string; // underlines, spine, highlights
  ink: string; // body text on light panels
  panel: string;
  panelBorder: string;
  coverBg: string;
  coverMotifA: string;
  coverMotifB: string;
  series: string[]; // chart bars, process steps
}

/** Normalises a theme hex ("0B5DAA") to a CSS/OOXML hex ("#0B5DAA"). */
function hex(color?: string): string | undefined {
  return color ? `#${color.replace(/^#/, "")}` : undefined;
}

/**
 * Maps a deck ThemeColors scheme onto the slide colour roles. Every role falls
 * back to the built-in scheme, so an absent or partial theme still renders.
 */
export function buildPalette(theme?: ThemeColors): SlidePalette {
  const a1 = hex(theme?.accent1);
  const a2 = hex(theme?.accent2);
  const a3 = hex(theme?.accent3);
  const primary = a1 ?? BLUE;
  const series = [a1, a2, a3, hex(theme?.accent4), hex(theme?.accent5), hex(theme?.accent6)].filter(
    (c): c is string => Boolean(c),
  );
  return {
    primary,
    accent: a2 ?? ACCENT,
    ink: hex(theme?.dk1) ?? INK,
    panel: PANEL,
    panelBorder: PANEL_BORDER,
    coverBg: hex(theme?.dk2) ?? primary,
    coverMotifA: a3 ?? LIGHT_BLUE,
    coverMotifB: hex(theme?.accent4) ?? TEAL,
    series: series.length >= 3 ? series : CHART_PALETTE,
  };
}

const DEFAULT_PALETTE = buildPalette();

/**
 * Shared header for content slides: the title plus a short accent underline, so
 * every slide reads as branded rather than a bare line of text.
 */
function contentHeader(title: string, p: SlidePalette): SlideElement[] {
  return [
    { ...titleEl(title), top: 44, fontSize: 30, fontColor: p.ink },
    { type: "shape", shape: "rectangle", left: 50, top: 104, width: 68, height: 5, fill: p.primary },
  ];
}

/**
 * The content-slide body: bullets in a rounded card with a left accent rail.
 * Splits into two columns once there are enough bullets that a single column
 * would run long, so busy slides stay readable.
 */
function contentBody(points: string[], p: SlidePalette): SlideElement[] {
  const bullets = points.length ? points : ["(no content)"];
  const card: SlideElement[] = [
    {
      type: "shape",
      shape: "roundedRectangle",
      left: 48,
      top: 150,
      width: 864,
      height: 322,
      fill: p.panel,
      borderColor: p.panelBorder,
      borderWidth: 1,
    },
    { type: "shape", shape: "rectangle", left: 48, top: 150, width: 8, height: 322, fill: p.primary },
  ];

  if (bullets.length > 4) {
    const mid = Math.ceil(bullets.length / 2);
    return [
      ...card,
      { type: "text", bullets: bullets.slice(0, mid), left: 84, top: 172, width: 386, height: 282, fontSize: 18, fontColor: p.ink, align: "left" },
      { type: "text", bullets: bullets.slice(mid), left: 496, top: 172, width: 396, height: 282, fontSize: 18, fontColor: p.ink, align: "left" },
    ];
  }
  return [
    ...card,
    { type: "text", bullets, left: 84, top: 172, width: 800, height: 282, fontSize: 19, fontColor: p.ink, align: "left" },
  ];
}

/** Deterministic 0..n-1 index from a seed string (stable across runs/tests). */
function pickVariant(seed: string, n: number): number {
  let h = 7;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + (seed.codePointAt(i) ?? 0)) % 100003;
  return h % n;
}

/* ── Cover variants (title deck opener) ─────────── */

const SUBTITLE_ON_DARK = "#DCE6F5";

/** Overlapping circles on the right + accent spine. */
function coverCircles(title: string, sub: string, p: SlidePalette): SlideElement[] {
  return [
    { type: "shape", shape: "ellipse", left: 604, top: 44, width: 312, height: 312, fill: p.coverMotifA },
    { type: "shape", shape: "ellipse", left: 720, top: 300, width: 196, height: 196, fill: p.coverMotifB },
    { type: "shape", shape: "rectangle", left: 0, top: 0, width: 12, height: 540, fill: p.accent },
    { ...titleEl(title), top: 206, fontColor: "#FFFFFF", fontSize: 48, width: 540 },
    { type: "shape", shape: "rectangle", left: 50, top: 300, width: 88, height: 6, fill: p.accent },
    { type: "text", text: sub, left: 50, top: 322, width: 520, height: 60, fontSize: 20, fontColor: SUBTITLE_ON_DARK },
  ];
}

/** Vertical colour stripes on the right in the theme's series colours. */
function coverStripes(title: string, sub: string, p: SlidePalette): SlideElement[] {
  const stripeW = 34;
  const gap = 14;
  const count = 4;
  const startX = 960 - (count * stripeW + (count - 1) * gap) - 56;
  const stripes = Array.from({ length: count }, (_, i): SlideElement => ({
    type: "shape",
    shape: "rectangle",
    left: startX + i * (stripeW + gap),
    top: 96,
    width: stripeW,
    height: 348,
    fill: p.series[i % p.series.length]!,
  }));
  return [
    ...stripes,
    { type: "shape", shape: "rectangle", left: 0, top: 0, width: 12, height: 540, fill: p.accent },
    { ...titleEl(title), top: 208, fontColor: "#FFFFFF", fontSize: 48, width: 520 },
    { type: "shape", shape: "rectangle", left: 50, top: 302, width: 88, height: 6, fill: p.accent },
    { type: "text", text: sub, left: 50, top: 324, width: 470, height: 60, fontSize: 20, fontColor: SUBTITLE_ON_DARK },
  ];
}

/** Solid colour panel down the right third with a circle accent. */
function coverPanel(title: string, sub: string, p: SlidePalette): SlideElement[] {
  return [
    { type: "shape", shape: "rectangle", left: 620, top: 0, width: 340, height: 540, fill: p.coverMotifA },
    { type: "shape", shape: "ellipse", left: 690, top: 178, width: 200, height: 200, fill: p.accent },
    { type: "shape", shape: "rectangle", left: 0, top: 0, width: 12, height: 540, fill: p.accent },
    { ...titleEl(title), top: 206, fontColor: "#FFFFFF", fontSize: 46, width: 540 },
    { type: "shape", shape: "rectangle", left: 50, top: 298, width: 88, height: 6, fill: p.accent },
    { type: "text", text: sub, left: 50, top: 320, width: 520, height: 60, fontSize: 20, fontColor: SUBTITLE_ON_DARK },
  ];
}

const COVER_VARIANTS = [coverCircles, coverStripes, coverPanel];

function coverModel(title: string, points: string[], p: SlidePalette): SlideModel {
  const build = COVER_VARIANTS[pickVariant(title, COVER_VARIANTS.length)]!;
  return { title, background: p.coverBg, elements: build(title, points[0] ?? "", p) };
}

/* ── Closing / "thank you" variants ─────────────── */

type ClosingModel = { background: string; elements: SlideElement[] };

/** Centred title on a neutral dark background. */
function closingCentered(title: string, sub: string, p: SlidePalette): ClosingModel {
  return {
    background: "#1F2937",
    elements: [
      { type: "text", text: title, left: 80, top: 200, width: 800, height: 80, fontSize: 40, bold: true, fontColor: "#FFFFFF", align: "center" },
      { type: "shape", shape: "rectangle", left: 440, top: 288, width: 80, height: 5, fill: p.accent },
      { type: "text", text: sub, left: 80, top: 306, width: 800, height: 40, fontSize: 18, fontColor: "#94A3B8", align: "center" },
    ],
  };
}

/** Left-aligned title on the brand colour with a corner circle. */
function closingLeft(title: string, sub: string, p: SlidePalette): ClosingModel {
  return {
    background: p.coverBg,
    elements: [
      { type: "shape", shape: "rectangle", left: 0, top: 0, width: 12, height: 540, fill: p.accent },
      { type: "shape", shape: "ellipse", left: 704, top: 336, width: 208, height: 208, fill: p.coverMotifA },
      { type: "text", text: title, left: 64, top: 208, width: 620, height: 90, fontSize: 52, bold: true, fontColor: "#FFFFFF", align: "left" },
      { type: "shape", shape: "rectangle", left: 66, top: 312, width: 90, height: 6, fill: p.accent },
      { type: "text", text: sub, left: 66, top: 334, width: 560, height: 50, fontSize: 20, fontColor: SUBTITLE_ON_DARK, align: "left" },
    ],
  };
}

/** Centred title flanked by two accent squares. */
function closingSquares(title: string, sub: string, p: SlidePalette): ClosingModel {
  return {
    background: p.coverBg,
    elements: [
      { type: "shape", shape: "rectangle", left: 372, top: 150, width: 36, height: 36, fill: p.accent },
      { type: "shape", shape: "rectangle", left: 552, top: 150, width: 36, height: 36, fill: p.coverMotifA },
      { type: "text", text: title, left: 80, top: 224, width: 800, height: 70, fontSize: 44, bold: true, fontColor: "#FFFFFF", align: "center" },
      { type: "shape", shape: "rectangle", left: 440, top: 312, width: 80, height: 5, fill: p.accent },
      { type: "text", text: sub, left: 80, top: 332, width: 800, height: 40, fontSize: 18, fontColor: SUBTITLE_ON_DARK, align: "center" },
    ],
  };
}

const CLOSING_VARIANTS = [closingCentered, closingLeft, closingSquares];

function closingModel(title: string, points: string[], p: SlidePalette): SlideModel {
  const build = CLOSING_VARIANTS[pickVariant(`${title}·close`, CLOSING_VARIANTS.length)]!;
  const { background, elements } = build(title, points[0] ?? "Thank you", p);
  return { title, background, elements };
}

/**
 * Builds a structured SlideModel from an outline row, choosing a layout that
 * suits the category — so the renderer exercises text, shapes, charts and
 * tables even without a backend. Real API data will replace this via the
 * `fromApiSlide` mapping when the JSON contract lands.
 */
export function slideModelFromOutline(
  title: string,
  points: string[],
  category?: SlideCategory,
  palette: SlidePalette = DEFAULT_PALETTE,
): SlideModel {
  const p = palette;

  switch (category) {
    case "COVER":
      return coverModel(title, points, p);

    case "STATISTICAL": {
      const cats = points.slice(0, 5).map((pt) => pt.split(" ").slice(0, 2).join(" "));
      return {
        title,
        elements: [
          ...contentHeader(title, p),
          {
            type: "chart",
            chart: "column",
            left: 48,
            top: 150,
            width: 520,
            height: 330,
            series: [{ name: "Value", data: pseudoSeries(title, Math.max(3, cats.length)) }],
            categories: cats.length ? cats : ["A", "B", "C"],
            colors: p.series,
            title: "By the numbers",
          },
          { type: "text", bullets: points.length ? points : ["(no content)"], left: 600, top: 150, width: 312, height: 330, fontSize: 18, fontColor: p.ink, align: "left" },
        ],
      };
    }

    case "COMPARISON":
      return {
        title,
        elements: [
          ...contentHeader(title, p),
          {
            type: "table",
            left: 48,
            top: 150,
            width: 864,
            height: 320,
            header: true,
            rows: [["Aspect", "Detail"], ...points.map((pt, i) => [`Point ${i + 1}`, pt])],
          },
        ],
      };

    case "PROCESS": {
      const steps = (points.length ? points : ["Step 1", "Step 2", "Step 3"]).slice(0, 4);
      const gap = 24;
      const w = (864 - gap * (steps.length - 1)) / steps.length;
      return {
        title,
        elements: [
          ...contentHeader(title, p),
          ...steps.map<SlideElement>((pt, i) => ({
            type: "shape",
            shape: "chevron",
            left: 48 + i * (w + gap),
            top: 230,
            width: w,
            height: 120,
            fill: p.series[i % p.series.length],
            text: pt,
            fontColor: "#FFFFFF",
            fontSize: 14,
          })),
        ],
      };
    }

    case "HIERARCHICAL": {
      const kids = (points.length ? points : ["A", "B", "C"]).slice(0, 3);
      const gap = 24;
      const w = (600 - gap * (kids.length - 1)) / kids.length;
      return {
        title,
        elements: [
          ...contentHeader(title, p),
          { type: "shape", shape: "roundedRectangle", left: 360, top: 160, width: 240, height: 70, fill: p.primary, text: title, fontColor: "#FFFFFF", fontSize: 16 },
          ...kids.map<SlideElement>((pt, i) => ({
            type: "shape",
            shape: "roundedRectangle",
            left: 180 + i * (w + gap),
            top: 320,
            width: w,
            height: 90,
            fill: "#E2E8F0",
            borderColor: p.primary,
            borderWidth: 1,
            text: pt,
            fontColor: "#1A1A1A",
            fontSize: 13,
          })),
        ],
      };
    }

    case "CLOSING":
      return closingModel(title, points, p);

    default:
      return {
        title,
        elements: [...contentHeader(title, p), ...contentBody(points, p)],
      };
  }
}
